/**
 * When to attach W3C trace context headers (`traceparent`, `sentry-trace`, `baggage`)
 * to outbound REST calls from the browser.
 *
 * If the SPA is served from a different hostname than {@link AppConfig.apiBaseUrl}
 * (e.g. chargeye.io → kempower.io API), adding these headers triggers a CORS preflight
 * unless every API Gateway allows those header names. We only attach when the page
 * origin matches the API host (same-origin to the API), or during full-stack local dev
 * where the page and the API are both on localhost.
 */

/** True for hostnames that resolve to the local machine. */
function isLocalHostname(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

/** Resolve hostname from `apiBaseUrl` as stored in config (host only, no path). */
export function hostnameFromApiBaseUrl(apiBaseUrl: string): string {
    const trimmed = apiBaseUrl.trim();
    try {
        if (/^https?:\/\//i.test(trimmed)) {
            return new URL(trimmed).hostname;
        }
        return new URL(`https://${trimmed}`).hostname;
    } catch {
        const noPath = trimmed.split('/')[0] ?? trimmed;
        return noPath.split(':')[0];
    }
}

/**
 * Returns true when trace headers may be sent on requests to the configured REST API
 * without cross-origin preflight issues for those headers.
 */
export function shouldAttachTraceHeadersToBackendRequests(apiBaseUrl: string): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const pageHost = window.location.hostname;
    const apiHost = hostnameFromApiBaseUrl(apiBaseUrl);
    if (isLocalHostname(pageHost)) {
        // Local frontend against a remote API is still cross-origin: attaching trace
        // headers there triggers a preflight that the deployed API Gateway rejects.
        // Only full-stack local dev (local page + local API) is safe.
        return isLocalHostname(apiHost);
    }
    return pageHost === apiHost;
}

/**
 * Sentry / rest-tracer targets: only HTTPS URLs for this environment's API host.
 * Do not merge with library defaults here — those defaults are service-wide and are
 * too broad for the browser SPA.
 */
export function buildBackendApiTracePropagationTargets(apiBaseUrl: string): RegExp[] {
    const escaped = hostnameFromApiBaseUrl(apiBaseUrl).replace(/\./g, String.raw`\.`);
    const portAndPath = String.raw`(?::\d+)?(?:/|$)`;
    return [new RegExp(`^https://${escaped}${portAndPath}`)];
}

/**
 * Returns true when the outbound request URL targets the configured API host.
 */
export function requestUrlTargetsConfiguredApi(reqUrl: string, apiBaseUrl: string): boolean {
    const apiHost = hostnameFromApiBaseUrl(apiBaseUrl);
    try {
        const u = new URL(reqUrl);
        return u.hostname === apiHost;
    } catch {
        try {
            const u = new URL(reqUrl, `https://${hostnameFromApiBaseUrl(apiBaseUrl)}`);
            return u.hostname === apiHost;
        } catch {
            return false;
        }
    }
}
